import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { GoogleLogo } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, pwd);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(extractApiError(err, "Login failed"));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background" data-testid="login-page">
      <div className="hidden lg:block relative africa-bg border-r border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-[hsl(var(--info))]/10" />
        <div className="relative h-full flex flex-col justify-between p-12 max-w-md">
          <Logo variant="wordmark" size={36} to="/" testId="login-logo" />
          <div>
            <div className="text-overline text-primary mb-4">The intelligence layer</div>
            <div className="font-display text-3xl xl:text-4xl leading-tight">
              Decisions worth millions deserve more than a deck of templates.
            </div>
            <div className="mt-8 flex gap-4 text-xs text-muted-foreground font-semibold uppercase tracking-wider">
              <div>54 Markets</div>
              <div className="h-3 w-px bg-border" />
              <div>9 AI Agents</div>
              <div className="h-3 w-px bg-border" />
              <div>Enterprise</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <Logo variant="wordmark" size={36} to="/" testId="login-mobile-logo" />
          </div>
          <div className="text-overline text-primary mb-3">Sign in</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">Welcome back.</h1>

          <button onClick={googleLogin} className="w-full border border-border bg-card rounded-lg py-3 flex items-center justify-center gap-3 hover:bg-secondary transition-colors mb-6 font-medium" data-testid="login-google">
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6 text-xs text-muted-foreground font-semibold uppercase tracking-wider">
            <div className="flex-1 h-px bg-border" /> OR <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-overline text-muted-foreground block mb-2">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" data-testid="login-email" />
            </div>
            <div>
              <label className="text-overline text-muted-foreground block mb-2">Password</label>
              <input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" data-testid="login-password" />
            </div>
            <button disabled={loading} type="submit" className="w-full bg-primary text-primary-foreground rounded-lg py-3 font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 card-elev" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-8 text-sm text-muted-foreground">
            No account? <Link to="/signup" className="text-primary underline underline-offset-4 font-semibold" data-testid="login-to-signup">Create one</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
